import { useState } from 'react';
import { PerformanceGroup } from '@/types/performance-analyze';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

interface ChartFiltersProps {
  groups: PerformanceGroup[];
  selectedGroupIds: string[];
  onGroupsChange: (groupIds: string[]) => void;
  windowSize: number;
  onWindowSizeChange: (size: number) => void;
  startDate: Date | undefined;
  endDate: Date | undefined;
  onDateRangeChange: (start: Date | undefined, end: Date | undefined) => void;
  onApply: () => void;
}

export const ChartFilters = ({
  groups,
  selectedGroupIds,
  onGroupsChange,
  windowSize,
  onWindowSizeChange,
  startDate,
  endDate,
  onDateRangeChange,
  onApply
}: ChartFiltersProps) => {
  // Group by symbol and prefix
  const groupedBySymbol = groups.reduce((acc, group) => {
    if (!acc[group.symbol]) {
      acc[group.symbol] = {};
    }
    const prefixKey = group.prefix || 'none';
    if (!acc[group.symbol][prefixKey]) {
      acc[group.symbol][prefixKey] = [];
    }
    acc[group.symbol][prefixKey].push(group);
    return acc;
  }, {} as Record<string, Record<string, PerformanceGroup[]>>);

  // Get unique prefixes across all symbols
  const allPrefixes = Array.from(
    new Set(groups.map(g => g.prefix).filter(Boolean))
  ) as string[];

  const toggleGroup = (groupId: string) => {
    if (selectedGroupIds.includes(groupId)) {
      onGroupsChange(selectedGroupIds.filter(id => id !== groupId));
    } else {
      onGroupsChange([...selectedGroupIds, groupId]);
    }
  };

  const selectAllByPrefix = (prefix: string) => {
    const prefixGroups = groups.filter(g => g.prefix === prefix).map(g => g.id);
    const newSelected = [...new Set([...selectedGroupIds, ...prefixGroups])];
    onGroupsChange(newSelected);
  };

  const clearAll = () => {
    onGroupsChange([]);
  };

  return (
    <div className="space-y-6 p-4 border rounded-lg bg-card">
      {/* Group Selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Select Groups to Compare</Label>
        
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {Object.entries(groupedBySymbol).map(([symbol, prefixGroups]) => (
            <div key={symbol} className="space-y-2">
              <h4 className="font-medium text-sm text-muted-foreground">{symbol}</h4>
              
              {Object.entries(prefixGroups).map(([prefix, symbolGroups]) => (
                <div key={prefix} className="ml-4 space-y-1">
                  {prefix !== 'none' && (
                    <p className="text-xs text-muted-foreground">PREFIX: {prefix}</p>
                  )}
                  
                  {symbolGroups.map(group => (
                    <div key={group.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={group.id}
                        checked={selectedGroupIds.includes(group.id)}
                        onCheckedChange={() => toggleGroup(group.id)}
                      />
                      <label
                        htmlFor={group.id}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {group.prefix || ''} {group.strategy}-{group.size_group} (
                        {group.tp_values.join(', ')})
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Select */}
      <div className="space-y-2">
        <Label className="text-sm">Quick Select</Label>
        <div className="flex flex-wrap gap-2">
          {allPrefixes.map(prefix => (
            <Button
              key={prefix}
              variant="outline"
              size="sm"
              onClick={() => selectAllByPrefix(prefix)}
            >
              Select All {prefix}
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={clearAll}>
            Clear All
          </Button>
        </div>
      </div>

      {/* Window Size */}
      <div className="space-y-2">
        <Label className="text-sm">Rolling Window</Label>
        <RadioGroup value={windowSize.toString()} onValueChange={(v) => onWindowSizeChange(parseInt(v))}>
          <div className="flex gap-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="3" id="3d" />
              <Label htmlFor="3d">3 days</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="7" id="7d" />
              <Label htmlFor="7d">7 days</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="14" id="14d" />
              <Label htmlFor="14d">14 days</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="30" id="30d" />
              <Label htmlFor="30d">30 days</Label>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Date Range */}
      <div className="space-y-2">
        <Label className="text-sm">Date Range</Label>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => onDateRangeChange(date, endDate)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => onDateRangeChange(startDate, date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Apply Button */}
      <Button onClick={onApply} className="w-full">
        Apply Filters
      </Button>
    </div>
  );
};
